import React from "react";
import classNames from "classnames/bind";
import { useTranslation } from "react-i18next";
import styles from "../authorization.module.scss";
import { CodeIcon, LockIcon, EyeIcon } from "../AuthIcons";
import { PasswordStrengthBar } from "../PasswordStrengthBar";

const cx = classNames.bind(styles);

interface ResetPasswordViewProps {
  verificationCode: string;
  newPassword: string;
  showNewPassword: boolean;
  codeError: boolean;
  passwordError: boolean;
  authError: string | null;
  isLoading: boolean;
  passwordStrength: number;
  passwordStrengthColor: string;
  passwordStrengthText: string;
  onCodeChange: (value: string) => void;
  onNewPasswordChange: (value: string) => void;
  onToggleNewPassword: () => void;
  onBackToSignIn: () => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
}

export const ResetPasswordView: React.FC<ResetPasswordViewProps> = ({
  verificationCode,
  newPassword,
  showNewPassword,
  codeError,
  passwordError,
  authError,
  isLoading,
  passwordStrength,
  passwordStrengthColor,
  passwordStrengthText,
  onCodeChange,
  onNewPasswordChange,
  onToggleNewPassword,
  onBackToSignIn,
  onSubmit,
}) => {
  const { t } = useTranslation();

  return (
    <form className={cx('form')} onSubmit={onSubmit}>
      <div className={cx('formHeader')}>
        <h2 className={cx('formTitle')}>{t("setNewPassword")}</h2>
        <p className={cx('formSubtitle')}>{t("enterCodeAndNewPassword")}</p>
      </div>

      {authError && <div className={cx('errorMessage')}>{t(authError)}</div>}

      <div className={cx('inputGroup')}>
        <label className={cx('inputLabel')}>{t("verificationCode")}</label>
        <div className={cx('inputWrapper', { inputError: codeError })}>
          <div className={cx('inputIcon')}>
            <CodeIcon />
          </div>
          <input
            name="code"
            type="text"
            className={cx('input')}
            placeholder={t("enterCode")}
            value={verificationCode}
            onChange={(e) => onCodeChange(e.target.value)}
          />
        </div>
        {codeError && <span className={cx('inputErrorText')}>{t("enterVerificationCode")}</span>}
      </div>

      <div className={cx('inputGroup')}>
        <label className={cx('inputLabel')}>{t("newPassword")}</label>
        <div className={cx('inputWrapper', { inputError: passwordError })}>
          <div className={cx('inputIcon')}>
            <LockIcon />
          </div>
          <input
            name="newPassword"
            type={showNewPassword ? "text" : "password"}
            className={cx('input')}
            placeholder={t("enterNewPassword")}
            value={newPassword}
            onChange={(e) => onNewPasswordChange(e.target.value)}
          />
          <button
            type="button"
            className={cx('passwordToggle')}
            onClick={onToggleNewPassword}
            tabIndex={-1}
          >
            <EyeIcon visible={showNewPassword} />
          </button>
        </div>
        {passwordError && <span className={cx('inputErrorText')}>{t("enterPassword")}</span>}
        {newPassword && (
          <PasswordStrengthBar
            strength={passwordStrength}
            strengthColor={passwordStrengthColor}
            strengthText={passwordStrengthText}
          />
        )}
      </div>

      <button type="submit" className={cx('submitButton')} disabled={isLoading}>
        {isLoading ? <span className={cx('spinner')}></span> : t("resetPasswordAction")}
      </button>

      <div className={cx('formFooter')}>
        <button
          type="button"
          onClick={onBackToSignIn}
          className={cx('backButton')}
        >
          &larr; {t("backToSignIn")}
        </button>
      </div>
    </form>
  );
};
